import AuthService, { validateToken } from '@dhi/react-components';
import React, { createContext, useEffect, useState } from 'react';
import Loading from '../../components/Loading';
import {
  clearTokenFromUrl,
  decodeToken,
  getTokenFromUrl,
  isTokenExpired,
  safeJsonParse,
} from '../../helpers/tokenAuth';
import { useStores } from '../../hooks/useStores';
import LoginPage from '../../login';
import { SessionUser } from '../../stores/types';
import { UserProviderProps } from './types';

const UserContext = createContext(null);
const { Provider, Consumer } = UserContext;
const authService = new AuthService(process.env.REACT_APP_API_AUTH_URL);

const UserProvider: React.FC<UserProviderProps> = ({ intl, children }: UserProviderProps) => {
  const session = authService.getSession();
  const { appStateStore } = useStores();
  const [isValidatingUrlToken, setIsValidatingUrlToken] = useState(true);
  const [urlTokenAuthenticated, setUrlTokenAuthenticated] = useState(false);

  // Handle URL token authentication on mount
  useEffect(() => {
    const initUrlTokenAuth = async () => {
      const urlToken = getTokenFromUrl();
      const isPasswordResetPath = window.location.pathname === '/password-reset';

      if (urlToken && !isPasswordResetPath) {
        console.log('URL token detected, validating...');

        const claims = decodeToken(urlToken);

        if (claims && !isTokenExpired(claims)) {
          try {
            // Validate token with auth server
            const response = await validateToken(process.env.REACT_APP_API_AUTH_URL, urlToken);

            if (response.ok) {
              console.log('URL token validated successfully');

              // Construct user object from JWT claims — equivalent to the User shape
              // produced by the standard AuthService login flow (id/name/userGroups/metadata).
              const nameClaim =
                claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
              const groupsClaim =
                claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/groupsid'];
              const user: SessionUser = {
                id: claims.sub,
                name: nameClaim || claims.sub,
                username: claims.sub,
                email: claims.email,
                userGroups: Array.isArray(groupsClaim)
                  ? groupsClaim
                  : groupsClaim
                  ? [groupsClaim]
                  : [],
                metadata: {
                  clientId: claims.clientid,
                  debug: claims.debug === 'True', // JWT claim is a string
                  modules: safeJsonParse<string[]>(claims.modules, []),
                  roles: safeJsonParse<string[]>(claims.roles, []),
                },
              };

              // Persist token + user to storage so the session survives a refresh,
              // matching what AuthService.setSession does on a standard login.
              // No refresh token is available from a URL-issued token.
              authService.setSession(
                {
                  accessToken: {
                    token: urlToken,
                    expiration: new Date(claims.exp * 1000).toISOString(),
                  },
                  refreshToken: { token: '' },
                },
                user as any,
                true,
              );

              // Wire it into the MobX store for the current render.
              appStateStore.setupSession(user, urlToken, '', claims.clientid, intl);
              setUrlTokenAuthenticated(true);

              // Clear token from URL for security
              clearTokenFromUrl();
            } else {
              console.warn('URL token validation failed');
            }
          } catch (error) {
            console.error('Error validating URL token:', error);
          }
        } else {
          console.warn('URL token is invalid or expired');
          // Clear invalid token from URL
          clearTokenFromUrl();
        }
      }

      setIsValidatingUrlToken(false);
    };

    initUrlTokenAuth();
  }, []);

  const handleLogin = (user) => {
    const loginSession = authService.getSession();
    const customerCode = user != null ? user.metadata.clientId : null;

    if (customerCode) {
      appStateStore.setupSession(user, loginSession.accessToken, loginSession.refreshToken, customerCode, intl);
      window.history.replaceState('', module.id, '/');
    }
  };

  const handleLogout = () => {
    appStateStore.logout(intl);
  };

  // Setup session from existing AuthService session
  // Skip if URL token authentication was used to prevent overwriting the URL token user
  if (
    !urlTokenAuthenticated &&
    session.user != null &&
    appStateStore.session.token.accessToken !== session.accessToken
  ) {
    appStateStore.setupSession(
      session.user,
      session.accessToken,
      session.refreshToken,
      session.user != null ? session.user.metadata.clientId : null,
    );
  }

  // Show loading while validating URL token
  if (isValidatingUrlToken) {
    return <Loading />;
  }

  const isAuthenticated = authService.isAuthenticated() || urlTokenAuthenticated;

  return (
    <Provider
      value={{
        onLogout: handleLogout,
      }}
    >
      {isAuthenticated ? children : <LoginPage onLogin={handleLogin}>{children}</LoginPage>}
    </Provider>
  );
};

export { Consumer as UserConsumer, UserProvider };
export default UserContext;
